import { useEffect } from 'react'
import ClickableText from './ClickableText'
import { useLocale } from '../../i18n/context'

const IMAGE_LABELS = new Set(['Image', 'Image URL'])
const AUDIO_LABELS = new Set(['Audio', 'Audio URL'])
const AUDIO_URL_RE = /^https?:\/\/.+\.(mp3|m4a|ogg|wav|webm|aac|opus|flac)(\?.*)?$/i

function isImageSide(s: { label: string; content: string }) {
  return IMAGE_LABELS.has(s.label) || s.content.startsWith('data:image/')
}

function isAudioSide(s: { label: string; content: string }) {
  return AUDIO_LABELS.has(s.label) || s.content.startsWith('data:audio/') || AUDIO_URL_RE.test(s.content.trim())
}

export default function ShowAllOverlay({
  sides, onClose, speak, isSpeechSupported,
}: {
  sides: { label: string; content: string }[]
  onClose: () => void
  speak: (word: string) => void
  isSpeechSupported: boolean
}) {
  const { tr } = useLocale()
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', borderRadius: 16,
          padding: '20px 20px', width: '100%', maxWidth: 480,
          boxShadow: 'var(--shadow-float)', maxHeight: '80vh', overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {tr.learn.allSides}
          </span>
          <button
            onClick={onClose}
            aria-label={tr.proUpgrade.close}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 22, lineHeight: 1, padding: '6px 8px', minHeight: 44, minWidth: 44 }}
          >
            ×
          </button>
        </div>
        {sides.map((s, i) => (
          <div key={i} style={{ borderTop: i > 0 ? '1px solid var(--border)' : undefined, paddingTop: i > 0 ? 16 : 0, paddingBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 18, fontFamily: 'var(--font-content)', color: 'var(--text-primary)', lineHeight: 1.5 }}>
              {isImageSide(s) ? (
                <img
                  src={s.content}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              ) : isAudioSide(s) ? (
                <audio
                  src={s.content}
                  controls
                  preload="metadata"
                  style={{ width: '100%', maxWidth: 320 }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <ClickableText text={s.content} speak={speak} isSpeechSupported={isSpeechSupported} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
